import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { DialogDefaultV2, TabFormV2 } from '../../../components/v2';
import TableService from '../../../services/TableService';
import {
  URL_DATE_FORMAT,
  agruparFilterRowsPorClassificacao,
  buildApiBody,
  buildDestinoHref,
  formatDiaTitulo,
  fraseCurta,
  inventarioDia,
  labelDiaSemana,
  labelSeveridade,
  significadoCompleto,
} from './constants';

const PAGE_SIZE = 50;

const blocoInventarioSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  p: 1,
  minHeight: 72,
  flex: 1,
};

const corSeveridade = (severidade) => {
  if (severidade === 'ALTA') return 'error';
  if (severidade === 'MEDIA') return 'warning';
  return 'default';
};

const formatDiaCompacto = (row) => {
  if (!row?.dia) return '—';
  const m = moment(row.dia, URL_DATE_FORMAT);
  const data = m.format('DD/MM');
  const semana = (labelDiaSemana(row.diaSemana) || m.locale('pt-br').format('dddd')).toLowerCase();
  return `${data} (${semana})`;
};

const formatDiasResumo = (dias, limite = 3) => {
  const lista = [...(dias || [])].sort((a, b) => String(a.dia || '').localeCompare(String(b.dia || '')));
  if (!lista.length) return '—';
  const visiveis = lista.slice(0, limite).map(formatDiaCompacto);
  const resto = lista.length - visiveis.length;
  if (resto <= 0) return visiveis.join(', ');
  return `${visiveis.join(', ')} + ${resto} ${resto === 1 ? 'dia' : 'dias'}`;
};

const abrirFonte = (fonte, { usuarioId, dia }) => {
  const inicio = dia ? moment(dia, URL_DATE_FORMAT).startOf('day').valueOf() : null;
  const fim = dia ? moment(dia, URL_DATE_FORMAT).endOf('day').valueOf() : null;
  window.open(buildDestinoHref(fonte, { usuarioId, inicio, fim }), '_blank', 'noopener,noreferrer');
};

const ChipFonteTemos = ({ fonte, usuarioId, dia }) => {
  const navegavel = Boolean(fonte.href && usuarioId);
  return (
    <Chip
      size="small"
      label={
        navegavel ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {fonte.label}
            <OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />
          </Box>
        ) : fonte.label
      }
      color="default"
      variant="outlined"
      onClick={navegavel ? () => abrirFonte(fonte, { usuarioId, dia }) : undefined}
      clickable={navegavel}
      sx={navegavel ? { cursor: 'pointer' } : undefined}
    />
  );
};

ChipFonteTemos.propTypes = {
  fonte: PropTypes.object.isRequired,
  usuarioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dia: PropTypes.string,
};

const ChipFonteFalta = ({ fonte }) => (
  <Chip
    size="small"
    label={fonte.label}
    color={fonte.ruim ? 'error' : 'default'}
    variant={fonte.ruim ? 'filled' : 'outlined'}
  />
);

ChipFonteFalta.propTypes = {
  fonte: PropTypes.object.isRequired,
};

const CardDiaLayoutC = ({ row, grupo, pessoa }) => {
  const inventario = inventarioDia(grupo.codigo, row);
  const faltasRuins = inventario.falta.filter((fonte) => fonte.ruim);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatDiaTitulo(row)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, mb: 0.5 }}>
            <Chip
              size="small"
              label={labelSeveridade(grupo.severidade) || '—'}
              color={corSeveridade(grupo.severidade)}
              variant="outlined"
            />
            <Typography variant="body2">{grupo.tipo}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {fraseCurta(grupo.codigo)}
          </Typography>
        </Box>

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={blocoInventarioSx}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
              Temos
            </Typography>
            {inventario.tem.length === 0 ? (
              <Typography variant="caption" color="text.secondary">Nenhuma evidência</Typography>
            ) : (
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {inventario.tem.map((fonte) => (
                  <ChipFonteTemos
                    key={fonte.key}
                    fonte={fonte}
                    usuarioId={pessoa.usuarioId}
                    dia={row.dia}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Box sx={blocoInventarioSx}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
              {faltasRuins.length ? 'Falta — esperado neste dia' : 'Falta'}
            </Typography>
            {inventario.falta.length === 0 ? (
              <Typography variant="caption" color="text.secondary">Nada ausente</Typography>
            ) : (
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {inventario.falta.map((fonte) => (
                    <ChipFonteFalta key={fonte.key} fonte={fonte} />
                  ))}
                </Stack>
                {faltasRuins.length > 0 && (
                  <Typography variant="caption" color="error.main">
                    {faltasRuins.map((fonte) => fonte.nota).filter(Boolean).join(' ')}
                  </Typography>
                )}
                {!faltasRuins.length && inventario.falta.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Ausente, mas não decide esta regra.
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

CardDiaLayoutC.propTypes = {
  row: PropTypes.object.isRequired,
  grupo: PropTypes.object.isRequired,
  pessoa: PropTypes.object.isRequired,
};

const AbaClassificacao = ({ grupo, pessoa }) => {
  const dias = useMemo(
    () => [...(grupo.dias || [])].sort((a, b) => String(a.dia || '').localeCompare(String(b.dia || ''))),
    [grupo.dias],
  );

  return (
    <Stack spacing={1.5}>
      {dias.map((row) => (
        <CardDiaLayoutC
          key={`${row.dia}-${grupo.codigo}`}
          row={row}
          grupo={grupo}
          pessoa={pessoa}
        />
      ))}
    </Stack>
  );
};

AbaClassificacao.propTypes = {
  grupo: PropTypes.object.isRequired,
  pessoa: PropTypes.object.isRequired,
};

const ResumoInspetorDialog = ({
  pessoa,
  codigo,
  severidade,
  inicio,
  fim,
  onClose,
  onOpenCalendario,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!pessoa?.usuarioId) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(false);
      setRows([]);
      try {
        const all = [];
        let page = 0;
        let total = Infinity;
        while (all.length < total && page < 20) {
          const response = await TableService.request(
            `reports/conformidade-inspecao/filter?p=${page}&s=${PAGE_SIZE}`,
            buildApiBody(inicio, fim, {
              usuarioIds: [pessoa.usuarioId],
              page,
              size: PAGE_SIZE,
            }),
          );
          const pageRows = response?.data?.rows || [];
          total = Number(response?.data?.totalRows) || pageRows.length;
          all.push(...pageRows);
          if (!pageRows.length) break;
          page += 1;
        }
        if (!cancelled) setRows(all);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [fim, inicio, pessoa]);

  const grupos = useMemo(() => agruparFilterRowsPorClassificacao(rows), [rows]);

  useEffect(() => {
    if (!codigo || !grupos.length) {
      setTab(0);
      return;
    }
    const idx = grupos.findIndex((grupo) => (
      grupo.codigo === codigo && (severidade == null || grupo.severidade === severidade)
    ));
    setTab(idx >= 0 ? idx + 1 : 0);
  }, [codigo, grupos, pessoa, severidade]);

  const abaResumo = (
    <Stack spacing={2}>
      {grupos.map((grupo, index) => (
        <Box
          key={`${grupo.codigo}-${grupo.severidade || 'sem'}`}
          onClick={() => setTab(index + 1)}
          sx={{
            cursor: 'pointer',
            p: 1.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
            <Chip
              size="small"
              label={labelSeveridade(grupo.severidade) || '—'}
              color={corSeveridade(grupo.severidade)}
              variant="outlined"
            />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {grupo.tipo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`${grupo.dias.length} ${grupo.dias.length === 1 ? 'dia' : 'dias'}`}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {significadoCompleto(grupo.codigo)}
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary', mr: 0.75 }}>
              Dias:
            </Box>
            {formatDiasResumo(grupo.dias)}
          </Typography>
        </Box>
      ))}
      {!loading && !error && !grupos.length && (
        <Typography variant="body2" color="text.secondary">Nenhuma inconsistência neste ciclo.</Typography>
      )}
    </Stack>
  );

  const tabPanels = [
    { id: 'resumo', label: 'Resumo', element: abaResumo },
    ...grupos.map((grupo) => ({
      id: `${grupo.codigo}-${grupo.severidade || 'sem'}`,
      label: `${grupo.chip} · ${grupo.dias.length}`,
      element: <AbaClassificacao grupo={grupo} pessoa={pessoa} />,
    })),
  ];

  const pior = labelSeveridade(pessoa?.piorSeveridade);
  const subtitulo = pessoa
    ? `${pessoa.regime} · ${pessoa.diasComOcorrencia} dias com ocorrência${pior ? ` · pior ${pior}` : ''}`
    : '';

  return (
    <DialogDefaultV2
      open={Boolean(pessoa)}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title={pessoa ? `${pessoa.usuarioNome} — Resumo` : 'Resumo'}
      readOnly
      maxWidth="lg"
      fullWidth
      headerAction={{
        title: 'Calendário',
        variant: 'outlined',
        show: Boolean(pessoa && onOpenCalendario),
        onClick: () => onOpenCalendario(pessoa),
      }}
    >
      {pessoa && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">{subtitulo}</Typography>
          {loading && <LinearProgress />}
          {error && (
            <Alert severity="error">Não foi possível carregar os dias deste inspetor.</Alert>
          )}
          {!error && (loading ? (
            <Typography variant="body2" color="text.secondary">
              {fraseCurta(codigo) || 'Carregando o ciclo desta pessoa.'}
            </Typography>
          ) : (
            <TabFormV2 tabPanels={tabPanels} currentTab={tab} onChangeTab={setTab} />
          ))}
        </Stack>
      )}
    </DialogDefaultV2>
  );
};

ResumoInspetorDialog.propTypes = {
  pessoa: PropTypes.object,
  codigo: PropTypes.string,
  severidade: PropTypes.string,
  inicio: PropTypes.number,
  fim: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onOpenCalendario: PropTypes.func,
};

export default ResumoInspetorDialog;
